import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import {
    IDLE, PENDING,
    LIST, ADD, EDIT, VIEW,
    DANGER, SUCCESS,
    REQUIREMENTS,
} from "const"
import { CcRequirement } from "server/models"
import { StateType, deepCopy, hasProjectAccess } from "client/utils"
import {
    PRM_CC_REQUIREMENT,
    PRM_CC_RESULTS_PDF,
    StoreType,
    RequirementList,
    RequirementFilters,
    RequirementsOnSelect,

    LoadCcRequirementDictionaries,
    LoadAllCcRequirements,
    LoadAllCcRequirementParentChild,
    PrepareRequirementsNestedIds,
    DeleteCcRequirement,
    SortCcRequirement,
    GetMaxCcRequirements,

    setCcRequirementDictionariesLoaded,
    setCcRequirementsOnSelect,
    setCcRequirementSelected,
    setCcRequirementList,
    setCcRequirementParentChild,
    setCcRequirementNested,
    setCcRequirementIds,
    setCcRequirementSort,
    setCcRequirementFilters,
    addModal,
    closeModal,
} from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    PageForm,
    Loader,
    Button,
    Feedback,

    LangReportWrapper,
    LangReportSwitcher,

    Table,
    TableSearchWrapper,
    SortRows,
    DeleteRow,
} from "client/components"

import ExportToPDF from "client/pages/cc/exportToPDF"

import AccessDenied from "client/parts/accessDenied"
import CcRequirementSearch from "client/pages/search/ccRequirementSearch"
import Columns from "./columns"

interface RequirementListProps {
    onSelectRequirements?: RequirementsOnSelect
}

const List = ({
    onSelectRequirements = null,
}: RequirementListProps) => {
    const baseRoute = useSelector((state: StoreType) => state.ccRequirement.baseRoute)
    const dictionariesLoaded = useSelector((state: StoreType) => state.ccRequirement.dictionariesLoaded)
    const selected = useSelector((state: StoreType) => state.ccRequirement.selected)

    const requirementList = useSelector((state: StoreType) => state.ccRequirement.requirementList)
    const requirementParentChild = useSelector((state: StoreType) => state.ccRequirement.requirementParentChild)
    const requirementGroupedIds = useSelector((state: StoreType) => state.ccRequirement.requirementGroupedIds)
    const requirementIds = useSelector((state: StoreType) => state.ccRequirement.requirementIds)
    const sortColumn = useSelector((state: StoreType) => state.ccRequirement.sortColumn)
    const sortOrder = useSelector((state: StoreType) => state.ccRequirement.sortOrder)
    const filters = useSelector((state: StoreType) => state.ccRequirement.filters)

    const project = useSelector((state: StoreType) => state.project.projectView)
    const packageSettings = useSelector((state: StoreType) => state.system.packageSettings)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [dictionaryLoadState, setDictionaryLoadState] = useState<StateType>(IDLE)
    const [loadState, setLoadState] = useState<StateType>(IDLE)
    const [showExport, setShowExport] = useState(false)

    useEffect(() => {
        if (dictionariesLoaded) return
        setDictionaryLoadState(PENDING)

        LoadCcRequirementDictionaries(userData.accountId, project.id, dispatch).then(() => {
            setDictionaryLoadState(IDLE)
            dispatch(setCcRequirementDictionariesLoaded(true))
        })
    }, [dictionariesLoaded])

    useEffect(() => {
        dispatch(setCcRequirementsOnSelect(onSelectRequirements))
        dispatch(setCcRequirementSelected(null))

        setLoadState(PENDING)
        Promise.all([
            LoadAllCcRequirements(project.id).then((res) => {
                dispatch(setCcRequirementList(res))
            }),
            LoadAllCcRequirementParentChild(project.id).then((res) => {
                dispatch(setCcRequirementParentChild(res))
            }),
        ]).then(() => {
            setLoadState(IDLE)
        })
    }, [])

    useEffect(() => {
        const requirementsFiltered = SortCcRequirement(Object.values(requirementList), sortColumn, sortOrder, filters)
        if (onSelectRequirements) {
            const sortedIds: number[] = []
            requirementsFiltered.forEach((row) => sortedIds.push(row.id))
            dispatch(setCcRequirementIds(sortedIds))
        } else {
            const tableGroupedIds = PrepareRequirementsNestedIds(requirementsFiltered, requirementParentChild)
            dispatch(setCcRequirementNested(tableGroupedIds))
        }
    }, [onSelectRequirements, requirementList, requirementParentChild, sortColumn, sortOrder, filters])

    const onSort: SortRows = (column: keyof CcRequirement, order) => {
        dispatch(setCcRequirementSort(column, order))
    }

    const onFilter = (newFilters: RequirementFilters) => {
        dispatch(setCcRequirementFilters(newFilters))
    }

    const onDelete: DeleteRow = (requirement: CcRequirement, callback) => {
        DeleteCcRequirement(requirement.id, requirement.projectId).then((removedRows) => {
            const success = removedRows === 1
            callback(success, () => {
                const requirementListCopy: RequirementList = requirementList ? deepCopy(requirementList) : {}
                delete requirementListCopy[requirement.id]
                dispatch(setCcRequirementList(requirementListCopy))
            })
        })
    }

    const onSelect = () => {
        const selectedRequirements: CcRequirement[] = []
        if (selected) {
            selected.forEach((id) => {
                selectedRequirements.push(requirementList[id])
            })
        }
        onSelectRequirements(selectedRequirements)
        dispatch(closeModal()) // close requirement list modal
    }

    // max number of requirements
    const requirementsNumber = Object.keys(requirementList).length
    const { maxRequirements, requirementFeedback } = GetMaxCcRequirements(packageSettings, requirementsNumber, lang)

    const maxRequirementsReached = maxRequirements !== null && requirementsNumber >= maxRequirements
    const maxRequirementsExceeded = maxRequirements !== null && requirementsNumber > maxRequirements

    const columns = Columns()

    const listAccess = hasProjectAccess(PRM_CC_REQUIREMENT, LIST)
    const addAccess = hasProjectAccess(PRM_CC_REQUIREMENT, ADD)
    const viewPdfAccess = hasProjectAccess(PRM_CC_RESULTS_PDF, VIEW)

    if (!listAccess) return <AccessDenied />
    return (
        <Container>
            <PageHeader>
                <PageTitle>{lang.requirements}</PageTitle>
                {
                    viewPdfAccess && (
                        <LangReportWrapper>
                            <LangReportSwitcher />
                            <Button
                                variant="success"
                                disabled={loadState === PENDING}
                                onClick={() => setShowExport(true)}
                            >
                                {lang.export}
                            </Button>
                        </LangReportWrapper>
                    )
                }
            </PageHeader>
            <PageContent>
                {
                    (
                        dictionaryLoadState === PENDING
                        || loadState === PENDING
                    ) && <Loader />
                }
                <TableSearchWrapper>
                    <CcRequirementSearch
                        id="requirementList"
                        rightOffset={addAccess && !onSelectRequirements}
                        filters={filters}
                        onFilter={onFilter}
                    />
                    {
                        addAccess && !onSelectRequirements && (
                            <Button
                                variant="success"
                                disabled={maxRequirementsReached}
                                onClick={() => dispatch(addModal({
                                    url: `${baseRoute}/${EDIT}/0`,
                                }))}
                            >
                                {lang.add}
                            </Button>
                        )
                    }
                </TableSearchWrapper>
                <Table
                    columns={columns}
                    dataList={requirementList}
                    dataGroupedIds={!onSelectRequirements ? requirementGroupedIds : null}
                    dataIds={onSelectRequirements ? requirementIds : null}
                    selectedIds={selected}
                    sortColumn={sortColumn}
                    sortOrder={sortOrder}
                    rowSize="fit"
                    showPagination
                    onSort={onSort}
                    onDelete={onDelete}
                />
                <PageForm>
                    <Feedback variant={maxRequirementsExceeded ? DANGER : SUCCESS}>{requirementFeedback}</Feedback>
                    {
                        onSelectRequirements
                        && <Button variant="success" onClick={onSelect}>{lang.select}</Button>
                    }
                </PageForm>

                <ExportToPDF
                    show={showExport}
                    defaultReport={REQUIREMENTS}
                    onClose={() => setShowExport(false)}
                />
            </PageContent>
        </Container>
    )
}
export default List
